/**
 * SectionVideoPills — single chip on the section header that summarises every
 * video slide in the section. Click → popover lists each video with its
 * slide position + Replace/Delete actions.
 *
 * Refactored Feb 2026 from the original per-video pill layout (which scaled
 * poorly past 2-3 videos: trainers had to scroll through "+N" overflow chips
 * to reach later items). The new design shows ONE chip regardless of count,
 * with all videos surfaced inside a single scrollable popover sorted by
 * slide position.
 *
 * Replace = DELETE-then-attach client-side (per scoping Q-a).
 * Delete  = single DELETE /modules/{id}/slides/{slide_id}.
 */
import { useMemo, useState } from 'react';
import { Film, ExternalLink, Trash2, RefreshCw, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Button } from '../ui/button';
import { useApi } from '../../hooks/useApi';
import { canonicalVideoUrl, videoSourceLabel } from '../../utils/videoUrl';

function VideoRow({ video, moduleId, onChanged, onReplace, onClose, canEdit = true }) {
  const { del } = useApi();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const { slide, slideNumber } = video;
  const url = canonicalVideoUrl(slide);
  const label = videoSourceLabel(slide);

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy — your browser blocked clipboard access.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete this ${label} video from slide ${slideNumber}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await del(`/modules/${moduleId}/slides/${slide.slide_id}`);
      toast.success('Video deleted');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReplace = () => {
    onClose?.();
    onReplace?.(slide);
  };

  return (
    <div
      className="p-3 border-b last:border-b-0 flex gap-3 items-start hover:bg-muted/30 transition-colors"
      data-testid={`video-row-${slide.slide_id}`}
    >
      {/* Poster + slide-number overlay */}
      <div className="relative shrink-0">
        {slide.media_poster_url ? (
          <img
            src={slide.media_poster_url}
            alt=""
            className="w-20 h-12 rounded object-cover bg-muted"
          />
        ) : (
          <div className="w-20 h-12 rounded bg-muted flex items-center justify-center">
            <Film className="w-5 h-5 text-muted-foreground/40" />
          </div>
        )}
        <span
          className="absolute -top-1.5 -left-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1
                     rounded-full text-[10px] font-bold bg-[var(--lumyn-content)] text-white border-2 border-background"
          title={`This video is slide ${slideNumber} in the section`}
          data-testid={`video-row-slide-number-${slide.slide_id}`}
        >
          {slideNumber}
        </span>
      </div>

      {/* Center column — title, caption, source, URL */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
          <span className="text-[10px] text-muted-foreground">·</span>
          <span className="text-[10px] text-muted-foreground">Slide {slideNumber}</span>
        </div>
        <p className="text-sm font-medium truncate">{slide.title || `${label} video`}</p>
        {slide.media_caption && (
          <p className="text-xs text-muted-foreground line-clamp-1">{slide.media_caption}</p>
        )}

        {url && (
          <div className="flex items-center gap-1 mt-1.5 text-[11px]">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="flex-1 truncate text-[var(--lumyn-content)] hover:underline"
              data-testid={`video-row-url-${slide.slide_id}`}
            >
              {url}
            </a>
            <button
              type="button"
              onClick={handleCopy}
              className="p-1 rounded hover:bg-background shrink-0"
              title="Copy URL"
              data-testid={`video-row-copy-${slide.slide_id}`}
            >
              {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="p-1 rounded hover:bg-background shrink-0"
              title="Open in new tab"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {/* Per-row actions — hidden for read-only viewers (trainers) */}
        {canEdit && (
          <div className="flex items-center gap-2 mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleReplace}
              disabled={busy}
              data-testid={`video-row-replace-${slide.slide_id}`}
              className="h-7 text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />Replace
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDelete}
              disabled={busy}
              data-testid={`video-row-delete-${slide.slide_id}`}
              className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            >
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Delete
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function SectionVideoPills({ slides, moduleId, onChanged, onReplace }) {
  const [open, setOpen] = useState(false);
  // If the parent didn't supply an onReplace callback, the surface is
  // effectively read-only (trainer mode). Hide the per-row Replace/Delete
  // buttons but keep the popover itself open so trainers can copy URLs and
  // see what's attached.
  const canEdit = typeof onReplace === 'function';

  // Identify every video slide and pre-compute its 1-indexed slide number
  // so the popover row + the overlay badge always agree. Sort by `order`
  // ascending (Q-b=a) — trainers expect "Slide 1" to appear before "Slide 3".
  const videos = useMemo(() => {
    const rows = (slides || [])
      .map((sl, i) => ({ slide: sl, slideNumber: i + 1 }))
      .filter((r) => r.slide.media_type === 'video');
    rows.sort((a, b) => a.slide.order - b.slide.order);
    return rows;
  }, [slides]);

  if (videos.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="section-videos-chip"
          title={`${videos.length} video${videos.length === 1 ? '' : 's'} in this section — click to manage`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium
                     bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)]
                     border border-[var(--lumyn-content)]/30
                     hover:bg-[var(--lumyn-content)]/20 transition-colors cursor-pointer"
        >
          <Film className="w-3 h-3" />
          Videos · {videos.length}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-0 max-h-[70vh] overflow-hidden flex flex-col"
        align="end"
        data-testid="section-videos-popover"
      >
        <div className="px-3 py-2 border-b bg-muted/30 shrink-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Videos in this section ({videos.length})
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Sorted by slide position. Click Replace to swap the source in-place, or Delete to remove.
          </p>
        </div>
        <div className="overflow-y-auto flex-1" data-testid="section-videos-popover-list">
          {videos.map((v) => (
            <VideoRow
              key={v.slide.slide_id}
              video={v}
              moduleId={moduleId}
              onChanged={onChanged}
              onReplace={onReplace}
              onClose={() => setOpen(false)}
              canEdit={canEdit}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
